import { useMemo, useCallback, Fragment } from "react"
import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { PageCard, PageCardContent } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { MotionDiv } from "components/motions"
import { EmptyData } from "components/EmptyData"
import { DiningIcon } from "components/icons"
import { ImageSkeletonCard } from "components/ImageCard"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useDining } from "extensions/iconsiam/hooks/dining"
import { DiningCard } from "extensions/iconsiam/components/domains/dining/DiningCard"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { HorizontalScrollBarWithButton } from "extensions/iconsiam/components/HorizontalScrollBar/HorizontalScrollBarWithButton"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"

export const DiningKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    isFetched,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()
  const theme = useTheme()
  const { t } = useTranslation()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )
  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const showSubCategoryFilter = categories.length > 0
  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}

  const {
    pageCard,
    pageCardContent,
    contentWrapper,
    gridContainer,
    gridItem,
    venueSelector,
    emptyDataWrapper,
    diningImageCard,
    subCategoryFilterBar,
    filterWrapper,
    filterContainer,
  } = styleOverrides

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "dining-page-scroll-pane"
  )

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            sx={contentWrapper}
          >
            <Box
              sx={gridContainer}
              className={showSubCategoryFilter || hasData ? "" : "emptyList"}
              ref={scrollRef}
            >
              {hasData ? (
                <>
                  {restaurants.pages.map((page, pageNum) => {
                    return (
                      <Fragment key={pageNum}>
                        {page.data.map((restaurant, index) => {
                          const isAd = restaurant?.properties?.isAd
                          const occupantId = restaurant?.id
                          const isNewShop = restaurant?.properties?.isNewShop
                          const isComingSoon =
                            restaurant?.properties?.isComingSoon
                          return (
                            <DiningCard
                              data={restaurant}
                              isNewShop={!isAd && isNewShop}
                              isComingSoon={!isAd && isComingSoon}
                              sx={diningImageCard}
                              key={`${
                                isAd ? "ad-" : "directory-item"
                              }${occupantId}`}
                            />
                          )
                        })}
                      </Fragment>
                    )
                  })}
                  {hasNextPage &&
                    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                      <ImageSkeletonCard
                        ref={
                          index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                        }
                        key={index}
                        sx={gridItem}
                      />
                    ))}
                </>
              ) : !isFetched ? (
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ImageSkeletonCard key={index} sx={gridItem} />
                ))
              ) : (
                <Box sx={emptyDataWrapper}>
                  <EmptyData
                    icon={emptyIcon}
                    primary={t("Coming soon")}
                    secondary={t("Stay tuned for the update")}
                  />
                </Box>
              )}
            </Box>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={filterByVenueId}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            {showSubCategoryFilter && (
              <Box sx={filterWrapper}>
                <HorizontalScrollBarWithButton
                  className="subCategory-filterbar"
                  sx={filterContainer}
                >
                  <CategoryFilterBar
                    sx={subCategoryFilterBar}
                    activeCategory={currentCategory}
                    categories={categories}
                    renderFilterButton={CategoryFilterChip}
                    onChange={handleChangeCategory}
                    enableSeparateBorder={false}
                  />
                </HorizontalScrollBarWithButton>
              </Box>
            )}
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
